import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Globe, Shield, ShieldCheck, Radar, Check, Loader2, MapPin } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { domainsApi, Domain, DomainStatus, DiscoveredDomain } from '../../lib/api/domains';
import { serversApi, Server } from '../../lib/api/servers';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';

const statusColors: Record<DomainStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-700',
};

export function DomainsPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [discoveryModalOpen, setDiscoveryModalOpen] = useState(false);
  const [selectedServer, setSelectedServer] = useState('');
  const [discoverySource, setDiscoverySource] = useState<'docker' | 'host' | 'both'>('both');
  const [discoveredDomains, setDiscoveredDomains] = useState<DiscoveredDomain[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [discoveryStep, setDiscoveryStep] = useState<'config' | 'results'>('config');

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['domains', page, search],
    queryFn: () => domainsApi.list({ page, limit: 20, search: search || undefined }),
  });

  const { data: serversData } = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list({ limit: 100 }),
  });

  const discoverMutation = useMutation({
    mutationFn: () =>
      domainsApi.discover({
        server_id: selectedServer,
        source: discoverySource,
        auto_import: false,
      }),
    onSuccess: (result) => {
      setDiscoveredDomains(result.discovered);
      // Pre-select new domains (ones without existing_domain_id)
      const newDomainNames = result.discovered
        .filter((d) => !d.existing_domain_id)
        .map((d) => d.name);
      setSelectedDomains(new Set(newDomainNames));
      setDiscoveryStep('results');
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      domainsApi.importDomains({
        server_id: selectedServer,
        domains: Array.from(selectedDomains),
        source: discoverySource,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] });
      closeDiscoveryModal();
    },
  });

  const openDiscoveryModal = () => {
    setDiscoveryModalOpen(true);
    setDiscoveryStep('config');
    setDiscoveredDomains([]);
    setSelectedDomains(new Set());
    setSelectedServer(serversData?.items?.[0]?.id || '');
  };

  const closeDiscoveryModal = () => {
    setDiscoveryModalOpen(false);
    setDiscoveryStep('config');
    setDiscoveredDomains([]);
    setSelectedDomains(new Set());
  };

  const toggleDomainSelection = (domainName: string) => {
    const newSelection = new Set(selectedDomains);
    if (newSelection.has(domainName)) {
      newSelection.delete(domainName);
    } else {
      newSelection.add(domainName);
    }
    setSelectedDomains(newSelection);
  };

  const selectAllDomains = () => {
    setSelectedDomains(new Set(discoveredDomains.map((d) => d.name)));
  };

  const selectNone = () => {
    setSelectedDomains(new Set());
  };

  const servers: Server[] = serversData?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Domains</h1>
          <p className="text-gray-600">Manage domains and SSL certificates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openDiscoveryModal}>
            <Radar className="h-4 w-4 mr-2" />
            Discover
          </Button>
          <Link to="/domains/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search domains..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="animate-pulse flex items-center gap-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Globe className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No domains</h3>
            <p className="text-gray-500 mb-4">Add your first domain or discover existing ones from nginx.</p>
            <div className="flex justify-center gap-2">
              <Button variant="outline" onClick={openDiscoveryModal}>
                <Radar className="h-4 w-4 mr-2" />
                Discover Domains
              </Button>
              <Link to="/domains/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Domain
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-3">
            {data?.items.map((domain: Domain) => (
              <Link key={domain.id} to={`/domains/${domain.id}`}>
                <Card className="hover:border-primary-300 transition-colors cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-primary-100">
                        <Globe className="h-5 w-5 text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{domain.name}</h3>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[domain.status]}`}>
                            {domain.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>{domain.dns_record_type} → {domain.dns_target || 'Not configured'}</span>
                          {domain.locations && domain.locations.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {domain.locations.length} location{domain.locations.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {domain.ssl_enabled ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <ShieldCheck className="h-4 w-4" />
                            <span className="text-sm">SSL</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-gray-400">
                            <Shield className="h-4 w-4" />
                            <span className="text-sm">No SSL</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {data && data.pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="flex items-center px-3 text-sm text-gray-600">
                Page {page} of {data.pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= data.pagination.total_pages}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* Discovery Modal */}
      <Modal
        isOpen={discoveryModalOpen}
        onClose={closeDiscoveryModal}
        title={discoveryStep === 'config' ? 'Discover Domains' : 'Discovery Results'}
        size="xl"
      >
        {discoveryStep === 'config' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Scan nginx configurations to discover existing domains. You can scan Docker nginx containers, host nginx, or both.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Server
              </label>
              <Select
                value={selectedServer}
                onChange={(e) => setSelectedServer(e.target.value)}
                options={servers.map((s) => ({ value: s.id, label: s.name }))}
                placeholder="Select a server"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discovery Source
              </label>
              <Select
                value={discoverySource}
                onChange={(e) => setDiscoverySource(e.target.value as 'docker' | 'host' | 'both')}
                options={[
                  { value: 'docker', label: 'Docker (tch-nginx container)' },
                  { value: 'host', label: 'Host (/etc/nginx/conf.d/)' },
                  { value: 'both', label: 'Both Docker and Host' },
                ]}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={closeDiscoveryModal}>
                Cancel
              </Button>
              <Button
                onClick={() => discoverMutation.mutate()}
                disabled={!selectedServer || discoverMutation.isPending}
              >
                {discoverMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Radar className="h-4 w-4 mr-2" />
                    Discover
                  </>
                )}
              </Button>
            </div>

            {discoverMutation.isError && (
              <p className="text-sm text-red-500">
                Discovery failed: {(discoverMutation.error as Error).message}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {discoveredDomains.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No domains found</h3>
                <p className="text-gray-500">No nginx configurations were found on the selected server.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Found {discoveredDomains.length} domain{discoveredDomains.length !== 1 ? 's' : ''}.
                    Select which ones to import.
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="text-sm text-primary-600 hover:text-primary-700"
                      onClick={selectAllDomains}
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      className="text-sm text-primary-600 hover:text-primary-700"
                      onClick={selectNone}
                    >
                      Select none
                    </button>
                  </div>
                </div>

                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {discoveredDomains.map((domain) => (
                    <div
                      key={domain.name}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedDomains.has(domain.name)
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => toggleDomainSelection(domain.name)}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center ${
                            selectedDomains.has(domain.name)
                              ? 'bg-primary-600 border-primary-600'
                              : 'border-gray-300'
                          }`}
                        >
                          {selectedDomains.has(domain.name) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{domain.name}</span>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${
                              domain.source === 'docker' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>
                              {domain.source}
                            </span>
                            {domain.ssl_enabled && (
                              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                SSL
                              </span>
                            )}
                            {domain.existing_domain_id && (
                              <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                Already exists
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {domain.config_file}
                          </p>
                          {domain.locations.length > 0 && (
                            <div className="mt-2 text-xs text-gray-600">
                              <span className="font-medium">Locations:</span>
                              <ul className="mt-1 space-y-0.5">
                                {domain.locations.map((loc, idx) => (
                                  <li key={idx} className="flex items-center gap-2">
                                    <span className="text-gray-400">{loc.path}</span>
                                    <span className="text-gray-300">→</span>
                                    <span>{loc.upstream_host}:{loc.upstream_port}</span>
                                    {loc.matched_app_name && (
                                      <span className="text-primary-600">({loc.matched_app_name})</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <Button variant="outline" onClick={() => setDiscoveryStep('config')}>
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={closeDiscoveryModal}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => importMutation.mutate()}
                      disabled={selectedDomains.size === 0 || importMutation.isPending}
                    >
                      {importMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        `Import ${selectedDomains.size} Domain${selectedDomains.size !== 1 ? 's' : ''}`
                      )}
                    </Button>
                  </div>
                </div>

                {importMutation.isError && (
                  <p className="text-sm text-red-500">
                    Import failed: {(importMutation.error as Error).message}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
